import React, { useState } from "react";
import {
  Header,
  Button,
  Icon,
  Popup,
  Progress,
  Label,
  Grid,
  Divider,
  Table,
  List
} from "semantic-ui-react";
import "../../components/SharedCard.css";
import { criterionOutcome, criterionPoints, evaluatedOutcome, utteranceUnit, CRITERION_ORANGE, SHOW_CHATLOG_EVENT, scrollToWhenReady } from "./evaluationRules";

const getCustomColor = (value) => {
  const colors = ["red", "red", "red", "orange", "yellow", "olive", "green"];
  const index = Math.round(((value + 1) * (colors.length - 1)) / 2);
  return colors[index];
};

const RelevantSentencesList = ({ relevantUtterances, idx2sent, idx2clid, unit }) => {
  if (!relevantUtterances || relevantUtterances.length === 0) {
    return (
      <div style={{ padding: '1em', backgroundColor: '#f9fafb', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
        <Icon name="info circle" /> 관련 {unit}이 없습니다.
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f9fafb', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
      <Table basic="very" striped compact style={{ margin: 0, backgroundColor: 'transparent' }}>
        <Table.Body>
          {relevantUtterances.map((relevant_utterance, index) => {
            const is_object = typeof relevant_utterance === "object";
            const utterance_id = is_object ? relevant_utterance.id : index + 1;
            const utterance = is_object
              ? idx2sent
                ? idx2sent[relevant_utterance.id]
                : ""
              : relevant_utterance;
            const chatlog_id = is_object ? idx2clid[utterance_id] : "";
            const chatlog_utterance = is_object ? relevant_utterance.utterance : "";
            const confidence = is_object ? relevant_utterance.confidence : 1;
            const color = getCustomColor(confidence);

            return (
              <Table.Row
                key={index}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (document.getElementById(chatlog_id)) scrollToWhenReady(chatlog_id);
                  // Phones: the transcript is another tab — ask the report to show it.
                  else window.dispatchEvent(new CustomEvent(SHOW_CHATLOG_EVENT, { detail: chatlog_id }));
                }}
              >
                <Table.Cell width={1} textAlign="center">
                  <Popup
                    content={`${(confidence * 100).toFixed(0)}% 신뢰도`}
                    trigger={<Icon name="circle" color={color} size="small" />}
                  />
                </Table.Cell>
                <Table.Cell>
                  {/* The number the evaluation text cites, in the trainee bubbles' blue — not the
                      ChatLog pk prefix it used to show, which nothing else on screen used. */}
                  {is_object && (
                    <Label size="mini" horizontal
                           style={{ marginRight: "0.5em", backgroundColor: "#007AFF", color: "white" }}>
                      {unit} {utterance_id}
                    </Label>
                  )}
                  {utterance}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

const EvaluatorCard = ({ evaluator, title, score }) => {
  if (!evaluator) return null;
  const outcome = criterionOutcome(score, !!evaluator.is_criterion_met);
  return (
    <div style={{ 
      backgroundColor: '#f9fafb', 
      borderRadius: '8px', 
      padding: '1em', 
      border: '1px solid rgba(0,0,0,0.05)',
      height: '100%'
    }}>
      <Header as="h5" dividing style={{ marginBottom: '0.5em' }}>{title}</Header>
      <div style={{ marginBottom: '0.5em' }}>
        <Label size="small" basic color={outcome.favorable ? "green" : "red"}>
          {outcome.label}
        </Label>
        <span style={{ marginLeft: '0.5em', fontSize: '0.9em', color: 'grey' }}>
          {(evaluator.confidence * 100).toFixed(0)}% 신뢰도
        </span>
      </div>
      <p style={{ fontSize: '0.9em', color: '#424245' }}>{evaluator.thought}</p>
    </div>
  );
};

const EvaluationResult = ({ result, score, isEvaluatorVisible, showEvaluators, toggleEvaluators }) => {
  const outcome = criterionOutcome(score, !!result.is_criterion_met);
  return (
    <div style={{ padding: '1.5em', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
      <Grid columns={2} stackable>
        <Grid.Column width={4}>
          <div style={{ textAlign: 'center', marginBottom: '1em' }}>
            <Label size="large" color={outcome.favorable ? "green" : "red"}>
              {outcome.deduction ? outcome.label : `기준 ${outcome.label}`}
            </Label>
            <div style={{ marginTop: '0.5em', fontSize: '0.9em', color: 'grey' }}>
              신뢰도 {(result.confidence * 100).toFixed(0)}%
            </div>
          </div>
        </Grid.Column>
        <Grid.Column width={12}>
          <Header as="h5" style={{ color: '#1d1d1f' }}>평가 근거</Header>
          <p style={{ color: '#424245' }}>{result.evaluation || outcome.sentence}</p>
          
          {isEvaluatorVisible && (
            <Button 
              size="mini" 
              basic 
              onClick={toggleEvaluators} 
              style={{ marginTop: '1em' }}
            >
              <Icon name={showEvaluators ? "chevron up" : "chevron down"} />
              AI 평가자 상세 보기
            </Button>
          )}
        </Grid.Column>
      </Grid>

      {showEvaluators && (
        <div style={{ marginTop: '1.5em' }}>
          <Grid columns={3} stackable style={{ margin: '0 -0.5em' }}>
            <Grid.Column style={{ padding: '0 0.5em', marginBottom: '1em' }}>
              <EvaluatorCard evaluator={result.evaluator_1} title="평가자 1" score={score} />
            </Grid.Column>
            <Grid.Column style={{ padding: '0 0.5em', marginBottom: '1em' }}>
              <EvaluatorCard evaluator={result.evaluator_2} title="평가자 2" score={score} />
            </Grid.Column>
            <Grid.Column style={{ padding: '0 0.5em', marginBottom: '1em' }}>
              <EvaluatorCard evaluator={result.evaluator_3} title="평가자 3" score={score} />
            </Grid.Column>
          </Grid>
        </div>
      )}
    </div>
  );
};

const EvaluationCard = ({ evaluatedcriterion }) => {
  const is_evaluator_visible = evaluatedcriterion.result && evaluatedcriterion.result.evaluator_1 !== undefined;
  const tags = (evaluatedcriterion.criterion.tags || "")
    .split(',')
    .map(t => t.trim())
    .filter(t => t && t !== 'deduction');

  const [showRelevantUtterances, setShowRelevantUtterances] = useState(false);
  const toggleRelevantUtterances = () => {
    setShowRelevantUtterances(!showRelevantUtterances);
  };

  const [showEvaluators, setShowEvaluators] = useState(false);
  const toggleEvaluators = () => {
    setShowEvaluators(is_evaluator_visible && !showEvaluators);
  };

  const [showContent, setShowContent] = useState(false);
  const toggleContent = () => {
    setShowContent(!showContent);
  };

  return (
    <div className="custom-card" style={{ padding: 0, overflow: 'hidden', marginBottom: '1em' }}>
      <div 
        style={{ 
          padding: '1.5em', 
          display: 'flex', 
          alignItems: 'flex-start', 
          cursor: 'pointer', 
          width: '100%',
          backgroundColor: evaluatedcriterion.is_postprocessing ? '#f9fafb' : 'white'
        }} 
        onClick={toggleContent}
      >
        {evaluatedcriterion.is_postprocessing && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="spinner" loading size="large" />
          </div>
        )}
        
        <div style={{ paddingTop: '0.2em' }}>
          <Icon 
            name={showContent ? "chevron down" : "chevron right"} 
            style={{ marginRight: '10px', color: 'grey', flexShrink: 0 }}
          />
        </div>
        
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <div style={{ marginBottom: '0.3em' }}>
            <Header as="h5" style={{ margin: 0, wordBreak: 'keep-all', lineHeight: '1.4em', color: '#1d1d1f' }}>
              {/* Same orange as the summary pane's 평가 기준 chips that scroll here. */}
              <span style={{ color: CRITERION_ORANGE, fontWeight: 700, marginRight: '0.5em' }}>
                {evaluatedcriterion.criterion.int_id + 1}.
              </span>
              {evaluatedcriterion.criterion.text}
            </Header>
          </div>
          {tags.length > 0 && (
            <div style={{ marginTop: '0.2em', fontSize: '0.85em', color: '#86868b' }}>
              {tags.map((tag, i) => (
                <span key={i} style={{ marginRight: '0.8em' }}>#{tag}</span>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginLeft: '1em', flexShrink: 0, paddingTop: '0.2em' }}>
           <Popup
            trigger={
              <Label 
                circular 
                color={evaluatedOutcome(evaluatedcriterion)?.favorable ? "green" : "red"}
                size="small"
              >
                {evaluatedcriterion.result && evaluatedcriterion.result.score ?
                  evaluatedcriterion.result.score
                  : criterionPoints(evaluatedcriterion)
                }
                <span style={{ opacity: 0.7, marginLeft: '2px' }}>
                  / {evaluatedcriterion.criterion.score}
                </span>
              </Label>
            }
            content={`배점: ${evaluatedcriterion.criterion.score}점`}
          />
        </div>
      </div>

      {evaluatedcriterion.is_postprocessed && evaluatedcriterion.result ? (
        showContent && (
          <>
            <div 
              onClick={toggleRelevantUtterances} 
              style={{ 
                cursor: 'pointer', 
                padding: '1em 1.5em', 
                backgroundColor: '#f9fafb', 
                borderTop: '1px solid rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                alignContent:'center',
                color: '#424245',
                fontSize: '0.9em',
                lineHeight: '1'
              }}
            >
               <Icon name={showRelevantUtterances ? "chevron down" : "chevron right"} style={{ marginRight: '0.5em' }} />
               <span style={{ fontWeight: '600' }}>관련 {utteranceUnit([evaluatedcriterion])} ({evaluatedcriterion.result.relevant_utterances ? evaluatedcriterion.result.relevant_utterances.length : 0})</span>
            </div>
            
            {showRelevantUtterances && evaluatedcriterion.result.relevant_utterances && (
              <RelevantSentencesList 
                relevantUtterances={evaluatedcriterion.result.relevant_utterances}
                idx2sent={evaluatedcriterion.result.idx2sent}
                idx2clid={evaluatedcriterion.result.idx2clid}
                unit={utteranceUnit([evaluatedcriterion])}
              />
            )}

            <EvaluationResult 
              result={evaluatedcriterion.result}
              score={evaluatedcriterion.criterion.score}
              isEvaluatorVisible={is_evaluator_visible}
              showEvaluators={showEvaluators}
              toggleEvaluators={toggleEvaluators}
            />
          </>
        )
      ) : (
        showContent && (
          <div style={{ padding: '1.5em', borderTop: '1px solid rgba(0,0,0,0.05)', textAlign: 'center', color: '#86868b' }}>
            <Icon name="spinner" loading /> 평가가 진행 중입니다...
          </div>
        )
      )}
    </div>
  );
};

export default EvaluationCard;
